/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ValidatorString } from "@adapptio/lib-blueprint";
import React, { useMemo } from "react";
import {
	CenterContent,
	CONTENT_ALIGN_VR,
	CONTENT_PADDING_VR,
	CONTENT_SIZE
} from "../../../../ui/components/CenterContent";
import { FieldGroup } from "../../../../ui/components/Fields/FieldGroup";
import { FieldRow } from "../../../../ui/components/Fields/FieldRow";
import { FieldText } from "../../../../ui/components/Fields/FieldText";
import { InspectModel } from "../../../../ui/components/Inspector/InspectModel";
import { Panel } from "../../../../ui/components/Panel";
import { RTC } from "../../../../ui/components/RTC";
import { useFieldState } from "../../../../ui/hooks/useFieldGroup";
import { TViewComponent } from "../../../../view/IView";
import { ViewEditorCreate } from "../ViewEditorCreate";

export const ViewEditorCreateMain: TViewComponent<ViewEditorCreate> = ({ view }) => {
	const viewModel = useMemo(() => view.getViewModel(), [ view ]);
	const specModel = useMemo(() => viewModel.getModel().props.spec.value.props, [ viewModel ]);

	const [ fieldState, setFieldState ] = useFieldState({
		id: {
			defaultValue: "",
			validators: [ ValidatorString({ required: true, min: 1 }) ]
		}
	});

	return (
		<Panel>
			<CenterContent
				size={CONTENT_SIZE.LARGE}
				verticalAlign={CONTENT_ALIGN_VR.TOP}
				verticalPadding={CONTENT_PADDING_VR.MEDIUM}
			>
				<RTC>
					<h2>Create New Page</h2>
				</RTC>
				<div>
					<FieldRow>
						<FieldGroup>
							<FieldText
								value={fieldState.id.value}
								validationErrors={fieldState.id.validationErrors}
								placeholder="View Name"
								grow={true}
								onChange={setFieldState.id}
							/>
						</FieldGroup>
					</FieldRow>
					<InspectModel model={specModel.title.value} depth={0} path={[ "$view", "title" ]} />
					<InspectModel
						model={specModel.description.value}
						depth={0}
						path={[ "$view", "description" ]}
					/>
					<InspectModel
						model={specModel.requireAuthenticatedUser}
						depth={0}
						path={[ "$view", "requireAuthenticatedUser" ]}
					/>
					<InspectModel model={specModel.params} depth={0} path={[ "$view", "params" ]} />
				</div>
			</CenterContent>
		</Panel>
	);
};
